import PQueue from 'p-queue';

import { carmaCsrAuthClient } from '@/services/instance/carmaCsrAuthClient';
import { getOr } from '@/utils/getOr';

import {
  CheckoutApiPayload,
  CheckoutApiResponse,
  CheckoutApiUpdateVehiclePayload,
  DiscountsResponse,
  ExtendedCoveragePayload,
  ExtendedCoverageResponse,
} from './CheckoutApi.types';

const apiCreateCheckoutQueue = new PQueue({ concurrency: 1 });

/**
 * Retrieve the current user cart
 */
export const getCheckouts = async () => {
  await apiCreateCheckoutQueue.onIdle();
  return apiCreateCheckoutQueue.add(() =>
    carmaCsrAuthClient.get(`me/checkouts`).json<CheckoutApiResponse[]>(),
  ) as Promise<CheckoutApiResponse[]>;
};

/**
 * Create a cart
 * @param sku The vehicle SKU
 */
export const createCheckout = async (sku: string) => {
  await apiCreateCheckoutQueue.onIdle();
  return apiCreateCheckoutQueue.add(() =>
    carmaCsrAuthClient
      .post<CheckoutApiPayload>(`me/checkouts`, {
        json: {
          vehicle: {
            sku,
          },
        },
      })
      .json(),
  ) as Promise<CheckoutApiPayload>;
};

/**
 * Replace the car in the cart
 * @param cartId The cart ID
 * @param sku The vehicle SKU
 */
export const switchVehicleCheckout = async (cartId: string, sku: string) => {
  await apiCreateCheckoutQueue.onIdle();
  return apiCreateCheckoutQueue.add(() =>
    carmaCsrAuthClient
      .patch<CheckoutApiUpdateVehiclePayload>(`me/checkouts/${cartId}/vehicle`, {
        json: {
          sku,
        },
      })
      .json(),
  ) as Promise<CheckoutApiUpdateVehiclePayload>;
};

/**
 * Update a cart
 * @param cartId The cart ID
 * @param payload The data to pass to update the cart
 */
export const updateCheckout = async (cartId: string, payload: CheckoutApiPayload) => {
  await apiCreateCheckoutQueue.onIdle();
  return apiCreateCheckoutQueue.add(() =>
    carmaCsrAuthClient.patch<CheckoutApiResponse>(`me/checkouts/${cartId}`, { json: payload }).json(),
  ) as Promise<CheckoutApiResponse>;
};

export const waitForCheckoutIdle = () => apiCreateCheckoutQueue.onIdle();

export const handleUpdateCheckoutError = (error: any): string => {
  let errorMessage = 'An unknown error happened, please try again later';
  if (error.response) {
    errorMessage = getOr(error.response.data, 'detail[0].msg', getOr(error.response.data, 'detail', errorMessage));
  }
  return errorMessage;
};

// Reference: https://app.clickup.com/t/6937126/CARMA-6844
export const getExtendedCoverageData = async (cartId: string): Promise<ExtendedCoverageResponse> => {
  return await carmaCsrAuthClient.get(`me/checkouts/${cartId}/addons`).json();
};

export const editExtendedCoverageData = async (cartId: string, payload: ExtendedCoveragePayload): Promise<any> => {
  await apiCreateCheckoutQueue.onIdle();
  return apiCreateCheckoutQueue.add(() =>
    carmaCsrAuthClient.post(`me/checkouts/${cartId}/addons`, { json: payload }).json(),
  );
};

/**
 * Retrieve promo code details for a cart
 * @param cartId The cart ID
 */
export const getDiscounts = async (cartId: string): Promise<DiscountsResponse> => {
  return await carmaCsrAuthClient.get(`me/checkouts/${cartId}/discounts`).json();
};

/**
 * Apply a promo code to a cart
 * @param cartId The cart ID
 * @param promoCode The promo code to apply
 */
export const applyPromoCode = async (cartId: string, promoCode: string): Promise<any> => {
  return await carmaCsrAuthClient
    .post(`me/checkouts/${cartId}/discounts`, {
      json: {
        actions: [
          {
            type: 'add',
            code: promoCode,
          },
        ],
      },
    })
    .json();
};

export const removePromoCode = async (cartId: string, promoId: string): Promise<any> => {
  const response = await carmaCsrAuthClient.post(`me/checkouts/${cartId}/discounts`, {
    json: {
      actions: [
        {
          id: promoId,
          type: 'remove',
        },
      ],
    },
  });
  return response.json();
};
